import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, CheckCircle, AlertCircle, Smartphone, User } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManualEsim = ({ customers = [] }) => {
  const { allCountries } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [packageId, setPackageId] = useState('');
  const [iccid, setIccid] = useState('');
  const [lpaCode, setLpaCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryQuery, setCountryQuery] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const [planName, setPlanName] = useState('');
  const [dataAmount, setDataAmount] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Filter users as admin types
  useEffect(() => {
    if (searchTerm.length > 1) {
      const filtered = customers.filter(c => 
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.name?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 8);
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, customers]);

  // Filter countries as admin types
  const countryResults = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return [];
    return (allCountries || [])
      .filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.id?.toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [countryQuery, allCountries]);

  const selectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm('');
    setFilteredUsers([]);
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    if (!packageId.trim()) {
      setError('Package ID is required');
      return;
    }
    if (!iccid.trim()) {
      setError('ICCID is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await axios.post(`${API}/admin/manual-esim`, {
        user_email: selectedUser.email,
        package_id: packageId.trim(),
        iccid: iccid.trim(),
        lpa_code: lpaCode.trim() || null,
        country_id: selectedCountry?.id || null,
        country_name: selectedCountry?.name || 'Manual Entry',
        plan_name: planName.trim() || 'Manual eSIM',
        data_amount: parseFloat(dataAmount) || 0,
        duration_days: parseInt(durationDays) || 30
      });

      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setSelectedUser(null);
        setPackageId('');
        setIccid('');
        setLpaCode('');
        setSelectedCountry(null);
        setCountryQuery('');
        setPlanName('');
        setDataAmount('');
        setDurationDays('');
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(response.data.error || 'Failed to create manual eSIM');
      }
    } catch (err) {
      console.error('Error creating manual eSIM:', err);
      setError(err.response?.data?.detail || 'Failed to create manual eSIM');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Smartphone size={28} />
          <h2 className="text-2xl font-bold">Manual eSIM Assignment</h2>
        </div>
        <p className="text-white/80">
          Use this tool to manually assign an eSIM to a user's account when the automated API flow is unavailable.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="manual-esim-success">
          <CheckCircle className="text-emerald-600" size={24} />
          <div>
            <p className="font-semibold text-emerald-800">eSIM Successfully Assigned</p>
            <p className="text-emerald-600 text-sm">The eSIM has been linked to the user's account.</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="manual-esim-error">
          <AlertCircle className="text-red-600" size={24} />
          <div>
            <p className="font-semibold text-red-800">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
        {/* User Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select User *
          </label>
          
          {selectedUser ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-200 rounded-full flex items-center justify-center">
                  <User className="text-emerald-700" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedUser.name || 'No Name'}</p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={clearSelectedUser}
                className="text-gray-500 hover:text-red-600 text-sm font-semibold"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users by email or name..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                  data-testid="manual-esim-user-search"
                />
              </div>
              
              {/* Search Results Dropdown */}
              {filteredUsers.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-64 overflow-y-auto">
                  {filteredUsers.map((user, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectUser(user)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                    >
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="text-gray-600" size={16} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{user.name || 'No Name'}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Required Fields */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Package ID *
            </label>
            <input
              type="text"
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              placeholder="e.g., roamify-pkg-12345"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
              required
              data-testid="manual-esim-package-id"
            />
            <p className="text-xs text-gray-500 mt-1">The Roamify package ID</p>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ICCID *
            </label>
            <input
              type="text"
              value={iccid}
              onChange={(e) => setIccid(e.target.value)}
              placeholder="e.g., 89012345678901234567"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black font-mono"
              required
              data-testid="manual-esim-iccid"
            />
            <p className="text-xs text-gray-500 mt-1">The unique eSIM identifier</p>
          </div>
        </div>

        {/* LPA Activation Code - enables one-tap install */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            LPA Activation Code
          </label>
          <input
            type="text"
            value={lpaCode}
            onChange={(e) => setLpaCode(e.target.value)}
            placeholder="LPA:1$smdp.example.com$ACTIVATION-CODE"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black font-mono"
            data-testid="manual-esim-lpa-code"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional. If provided, the user will get one-tap install on iOS and Android plus a QR code in their dashboard.
            Format: <span className="font-mono">LPA:1$smdp_address$matching_id</span>
          </p>
        </div>

        {/* Optional Fields */}
        <div className="border-t border-gray-100 pt-6">
          <p className="text-sm font-semibold text-gray-700 mb-4">Optional Details</p>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm text-gray-600 mb-2">Country/Destination</label>
              {selectedCountry ? (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    {selectedCountry.flag && (
                      <img src={selectedCountry.flag} alt={selectedCountry.name} className="w-7 h-5 object-cover rounded" />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{selectedCountry.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{selectedCountry.id}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedCountry(null); setCountryQuery(''); }}
                    className="text-gray-500 hover:text-red-600 text-sm font-semibold"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={countryQuery}
                    onChange={(e) => { setCountryQuery(e.target.value); setShowCountryList(true); }}
                    onFocus={() => setShowCountryList(true)}
                    placeholder="Search country (e.g., Japan, JP, Europe)"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                    data-testid="manual-esim-country-search"
                  />
                  {showCountryList && countryResults.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-64 overflow-y-auto">
                      {countryResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => { setSelectedCountry(c); setCountryQuery(''); setShowCountryList(false); }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                        >
                          {c.flag && <img src={c.flag} alt={c.name} className="w-7 h-5 object-cover rounded" />}
                          <div>
                            <p className="font-semibold text-gray-900">{c.name}</p>
                            <p className="text-xs text-gray-500 font-mono">{c.id}{c.isRegion ? ' • Regional' : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2">Plan Name</label>
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g., 10GB - 30 Days"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                data-testid="manual-esim-plan-name"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2">Data Amount (GB)</label>
              <input
                type="number"
                value={dataAmount}
                onChange={(e) => setDataAmount(e.target.value)}
                placeholder="e.g., 10"
                step="0.1"
                min="0"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                data-testid="manual-esim-data-amount"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2">Duration (Days)</label>
              <input
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                placeholder="e.g., 30"
                min="1"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-black"
                data-testid="manual-esim-duration"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading || !selectedUser || !packageId || !iccid}
            className="w-full px-6 py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="manual-esim-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Assigning eSIM...
              </>
            ) : (
              <>
                <Smartphone size={20} />
                Assign eSIM to User
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> This tool is intended for emergency use only when the automated eSIM generation flow is unavailable. 
          The eSIM must already exist in the Roamify system. This action will link the eSIM to the user's account 
          so they can view it in their dashboard.
        </p>
      </div>
    </div>
  );
};

export default ManualEsim;
